import { useCallback, useEffect, useRef, useState } from 'react';
import { IS_DEMO } from '../constants/featureFlags';
import { fetchUnreadReplies, markReplySeen } from '../utils/feedbackReplies';

/**
 * Replies to the signed-in user's own Send Feedback messages that they have
 * not acknowledged yet (v3.90.0).
 *
 * One bounded query per signed-in page load (the app shell mounts this once;
 * route changes do not re-run it) — plus the same query once more only when
 * a "Got it" write is refused. It is a notice, not a page: a failed or
 * denied read shows nothing and is logged — the user loses no data, and the
 * reply is still there on the next load and under Send Feedback → Your
 * messages.
 *
 * "Got it" is optimistic: the card goes at once and the write follows, so a
 * slow or offline device never shows a stuck button. If the write is refused
 * the list is read again — a reply already acknowledged on another device is
 * simply gone, and one that really could not be saved comes back with an
 * error.
 *
 * `readOnly` (an administrator viewing as the user) never writes: the reply
 * stays unread for the person it was written to.
 *
 * @returns {{ replies: object[], error: Error|null,
 *             acknowledge: (id: string) => Promise<void>,
 *             forget: (ids: string[]) => void }}
 */
export function useFeedbackReplies(uid, { enabled = true, readOnly = false } = {}) {
  const [replies, setReplies] = useState([]);
  const [error, setError] = useState(null);
  // The uid the current list belongs to: a result for an earlier user is dropped.
  const uidRef = useRef(uid);
  uidRef.current = enabled ? uid : null;
  const repliesRef = useRef(replies);
  repliesRef.current = replies;

  useEffect(() => {
    // A different user (or none) never sees the previous user's replies,
    // whatever the new read does.
    setReplies((prev) => (prev.length ? [] : prev));
    setError(null);
    if (!enabled || !uid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const items = await fetchUnreadReplies(uid);
        if (!cancelled) setReplies(items);
      } catch (err) {
        console.error('Feedback replies could not be loaded:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [uid, enabled]);

  /** Drop replies the user has just read somewhere else (the Your messages list). */
  const forget = useCallback((ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    setReplies((prev) => prev.filter((r) => !ids.includes(r.id)));
  }, []);

  const acknowledge = useCallback(async (id) => {
    if (readOnly || !id || !uid) return;
    const position = repliesRef.current.findIndex((r) => r.id === id);
    const removed = position >= 0 ? repliesRef.current[position] : null;
    setError(null);
    setReplies((prev) => prev.filter((r) => r.id !== id));
    try {
      await markReplySeen(id);
    } catch (err) {
      console.error('Feedback reply could not be marked as seen:', err);
      // What is true now? Acknowledged elsewhere → not in the list, nothing to
      // show. Still unread → the card returns, with the error. If that cannot
      // be read either, this one card is put back (never the whole earlier
      // list: another reply may have been acknowledged since).
      let stillUnread = null;
      try {
        stillUnread = await fetchUnreadReplies(uid);
      } catch (readErr) {
        console.error('Feedback replies could not be re-read:', readErr);
      }
      if (uidRef.current !== uid) return;
      if (stillUnread) {
        setReplies(stillUnread);
        if (stillUnread.some((r) => r.id === id)) setError(err);
      } else {
        if (removed) {
          setReplies((prev) => {
            if (prev.some((r) => r.id === id)) return prev;
            const next = [...prev];
            next.splice(Math.min(position, next.length), 0, removed); // back where it was
            return next;
          });
        }
        setError(err);
      }
    }
  }, [readOnly, uid]);

  return { replies, error, acknowledge, forget };
}

/**
 * The app shell's use of the hook, in one tested place: every signed-in
 * session except demo (the demo project denies feedback writes and its one
 * login is shared by every visitor), read-only while an administrator is
 * viewing as the user.
 */
export function useSessionFeedbackReplies(user, isImpersonating) {
  return useFeedbackReplies(user?.uid, {
    enabled: !!user?.uid && !IS_DEMO,
    readOnly: !!isImpersonating,
  });
}
